/**
 * Server-backed visit handoff helpers — keep check-in + guest profile in sync
 * across code entry → welcome → hub → analysis.
 */

import {
  getActiveCheckinSession,
  setActiveCheckinSession,
  markSalonFaceCheckedIn,
  readVisitHandoff,
  writeVisitHandoff,
  clearVisitHandoff,
  persistSelectedServicesForRecommendations,
  getSalonFaceUserId,
  getSalonVisitReference,
  getSalonPendingCheckinId,
  clearSalonPendingCheckin,
} from './salonFaceSession';
import { fetchActiveVisit } from '../../salon-admin/utils/checkinQueueApi';
import { persistFaceProfileSnapshot } from '../../face-identity/utils/faceIdentityApi';
import { resolveFacePortraitUrl } from '../../face-identity/utils/facePortrait';

export {
  readVisitHandoff,
  writeVisitHandoff,
  clearVisitHandoff,
  persistSelectedServicesForRecommendations,
  readSelectedServiceNames,
  clearVisitLocalState,
  clearDeviceAfterVisitFinish,
} from './salonFaceSession';

/** Pull guest identity fields from check-in row + snapshot (passport / walk-in). */
export function customerFieldsFromCheckin(checkin = {}, extras = {}) {
  const snap = checkin?.customer_snapshot && typeof checkin.customer_snapshot === 'object'
    ? checkin.customer_snapshot
    : {};
  const profile = snap.profile && typeof snap.profile === 'object' ? snap.profile : {};
  const prefs =
    profile.preferences && typeof profile.preferences === 'object' ? profile.preferences : {};
  const faceUserId = String(
    checkin?.face_user_id || snap.face_user_id || extras.faceUserId || '',
  ).trim();
  const customerName = String(
    checkin?.customer_name || snap.name || snap.full_name || extras.customerName || '',
  ).trim();
  const mobile = String(
    checkin?.mobile || snap.mobile || snap.phone || extras.mobile || '',
  ).trim();
  const email = String(snap.email || profile.email || extras.email || '').trim();
  const snapUser =
    snap.user && typeof snap.user === 'object' ? snap.user : {};
  const gender = String(
    checkin?.gender ||
      checkin?.customer_gender ||
      snap.gender ||
      snap.sex ||
      snapUser.gender ||
      profile.gender ||
      prefs.gender ||
      prefs.Gender ||
      extras.gender ||
      '',
  ).trim();
  const dob = String(
    snap.date_of_birth ||
      snap.dob ||
      prefs.date_of_birth ||
      prefs.dob ||
      profile.date_of_birth ||
      extras.dob ||
      '',
  ).trim();
  const ageRaw = snap.age ?? profile.age ?? extras.age;
  const age =
    ageRaw != null && String(ageRaw).trim() !== '' ? String(ageRaw).trim() : '';
  return {
    faceUserId,
    customerName,
    mobile,
    email,
    gender,
    dob,
    age,
    organizationId: String(
      checkin?.organization_id || snap.organization_id || extras.organizationId || '',
    ).trim(),
  };
}

export function handoffFromCheckin(checkin, extras = {}) {
  if (!checkin?.id) return null;
  const snap = checkin.customer_snapshot || {};
  const selectedServices = checkin.selected_services || snap.selected_services || [];
  const fields = customerFieldsFromCheckin(checkin, extras);
  const portraitUrl = resolveFacePortraitUrl(snap, checkin, extras);
  const payload = {
    checkinId: checkin.id,
    customerCode: checkin.customer_code || '',
    status: checkin.status || 'in_service',
    faceUserId: fields.faceUserId,
    organizationId: fields.organizationId,
    stylistId: checkin.stylist_id || snap.stylist_id || '',
    stylistName: checkin.stylist_name || snap.stylist_name || '',
    selectedServices,
    entrySource: checkin.source || snap.entry_source || extras.entrySource || '',
    visitReference:
      snap.reference_token ||
      snap.session_reference ||
      snap.salon_reference ||
      extras.visitReference ||
      '',
    customerName: fields.customerName,
    mobile: fields.mobile,
    email: fields.email,
    gender: fields.gender,
    dob: fields.dob,
    age: fields.age,
    visitIntent: snap.visit_intent || snap.visitIntent || extras.visitIntent || '',
    ...(portraitUrl
      ? { captureUrl: portraitUrl, face_image: portraitUrl, photo_url: portraitUrl }
      : {}),
  };
  writeVisitHandoff(payload);
  setActiveCheckinSession({
    id: payload.checkinId,
    code: payload.customerCode,
    status: payload.status,
    faceUserId: payload.faceUserId,
  });
  return payload;
}

/** Resolve open visit from server when in-memory state was cleared by reload. */
export async function resolveAndRestoreActiveVisit() {
  const existing = getActiveCheckinSession();
  if (existing?.id && existing.status !== 'completed' && existing.status !== 'cancelled') {
    return existing;
  }

  let faceUserId = getSalonFaceUserId();
  let visitReference = getSalonVisitReference();
  let checkinId = existing?.id || null;
  const handoff = readVisitHandoff();
  if (handoff?.faceUserId) faceUserId = faceUserId || handoff.faceUserId;
  if (handoff?.visitReference) visitReference = visitReference || handoff.visitReference;
  if (handoff?.checkinId) checkinId = checkinId || handoff.checkinId;

  if (!faceUserId && !visitReference && !checkinId) return null;

  const checkin = await fetchActiveVisit({ faceUserId, visitReference, checkinId });
  if (!checkin?.id) return null;

  handoffFromCheckin(checkin, { faceUserId, visitReference });
  return getActiveCheckinSession();
}

export function applyCheckinToGuestSession(checkin, { markCheckedIn = false } = {}) {
  const handoff = handoffFromCheckin(checkin);
  if (!handoff) return null;
  persistSelectedServicesForRecommendations(handoff.selectedServices || []);

  const snap = checkin.customer_snapshot || {};
  const visitIntent = handoff.visitIntent || snap.visit_intent || snap.visitIntent || '';
  const profile = snap.profile && typeof snap.profile === 'object' ? { ...snap.profile } : {};
  const preferences =
    profile.preferences && typeof profile.preferences === 'object'
      ? { ...profile.preferences }
      : {};
  if (handoff.dob && !preferences.date_of_birth && !preferences.dob) {
    preferences.date_of_birth = handoff.dob;
  }
  if (Object.keys(preferences).length) {
    profile.preferences = preferences;
  }

  const localUserId = handoff.faceUserId || `checkin:${handoff.checkinId}`;
  persistFaceProfileSnapshot({
    faceUserId: handoff.faceUserId || '',
    user: {
      id: localUserId,
      name: handoff.customerName,
      full_name: handoff.customerName,
      mobile: handoff.mobile,
      email: handoff.email || '',
      gender: handoff.gender || '',
      age: handoff.age || '',
      date_of_birth: handoff.dob || '',
      dob: handoff.dob || '',
      organization_id: handoff.organizationId,
      profile,
      visit_intent: visitIntent,
    },
    profile,
    selectedServices: handoff.selectedServices,
    checkinId: handoff.checkinId,
    checkinCode: handoff.customerCode,
    visitIntent,
  });

  if (markCheckedIn) {
    markSalonFaceCheckedIn({
      faceUserId: handoff.faceUserId || '',
      mobile: handoff.mobile,
      name: handoff.customerName,
      email: handoff.email,
      gender: handoff.gender,
      dob: handoff.dob,
      age: handoff.age,
      organizationId: handoff.organizationId,
      visitReference: handoff.visitReference,
      entrySource: handoff.entrySource || 'salon_guest_confirm',
      checkinId: handoff.checkinId,
      checkinCode: handoff.customerCode,
      checkinStatus: handoff.status,
      comparePrevious: !snap.is_new_member,
    });
  }
  return handoff;
}

/** Route state for hub → skin/hair so analysis pages keep guest identity. */
export function buildAnalysisNavState(checkin, extras = {}) {
  const handoff = applyCheckinToGuestSession(checkin, { markCheckedIn: true }) || {};
  const fields = customerFieldsFromCheckin(checkin, extras);
  const customerName = fields.customerName || handoff.customerName || '';
  const mobile = fields.mobile || handoff.mobile || '';
  const email = fields.email || handoff.email || '';
  const gender = fields.gender || handoff.gender || '';
  const age = fields.age || handoff.age || '';
  const dob = fields.dob || handoff.dob || '';
  const faceUserId = fields.faceUserId || handoff.faceUserId || '';
  return {
    ...extras,
    checkinId: handoff.checkinId || checkin?.id,
    checkinCode: handoff.customerCode || checkin?.customer_code,
    customerName,
    mobile,
    email,
    gender,
    age,
    dob,
    faceUserId,
    visitIntent: handoff.visitIntent || extras.visitIntent || '',
    faceProfile: {
      faceUserId,
      user: {
        id: faceUserId || `checkin:${checkin?.id || ''}`,
        name: customerName,
        full_name: customerName,
        mobile,
        email,
        gender,
        age,
        date_of_birth: dob,
        dob,
      },
    },
  };
}

/** After scan/register, link newly enrolled face to a partner code without face_user_id. */
export async function linkPendingCheckinIfNeeded(faceUserId, extras = {}) {
  if (!faceUserId) return null;
  let checkinId = extras.pendingCheckinId || extras.checkinId || getSalonPendingCheckinId() || null;
  if (!checkinId) {
    const handoff = readVisitHandoff();
    if (handoff?.checkinId && !handoff.faceUserId) checkinId = handoff.checkinId;
  }
  if (!checkinId) return null;

  const { linkFaceToCheckin } = await import('../../salon-admin/utils/checkinQueueApi');
  const updated = await linkFaceToCheckin(checkinId, {
    faceUserId,
    customerSnapshot: extras.customerSnapshot || {
      face_user_id: faceUserId,
      name: extras.name,
      mobile: extras.mobile,
    },
  });
  clearSalonPendingCheckin();
  if (updated) handoffFromCheckin(updated, { faceUserId });
  return updated;
}

/**
 * Clear local visit handoff on kiosk logout. When complete is false, cancels open
 * active visits or force-cancels in-service visits on the server when possible.
 * Must run while franchise-flow token is still valid.
 */
export async function finalizeOpenVisitOnLogout({ complete = false } = {}) {
  const { completeCheckinQueue, cancelCheckinQueue } = await import(
    '../../salon-admin/utils/checkinQueueApi'
  );
  const { clearActiveCheckinSession, getActiveCheckinSession } = await import(
    './salonFaceSession'
  );

  let session = getActiveCheckinSession();
  if (!session?.id || session.status === 'completed' || session.status === 'cancelled') {
    try {
      session = (await resolveAndRestoreActiveVisit()) || session;
    } catch {
      /* ignore */
    }
  }

  if (!session?.id) {
    clearActiveCheckinSession();
    clearVisitHandoff();
    return null;
  }

  const status = String(session.status || '').toLowerCase();
  if (status === 'completed' || status === 'cancelled') {
    clearActiveCheckinSession();
    clearVisitHandoff();
    return session;
  }

  try {
    if (complete) {
      await completeCheckinQueue(session.id);
    } else if (status === 'active') {
      await cancelCheckinQueue(session.id, { force: false });
    } else {
      await cancelCheckinQueue(session.id, { force: true });
    }
    try {
      window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
    } catch {
      /* ignore */
    }
  } catch {
    /* still clear local so logout can proceed */
  }

  clearActiveCheckinSession();
  clearVisitHandoff();
  return session;
}
